import { supabase } from '@/integrations/supabase/client';
import { PropertyPhoto } from '@/types/property';

export type FullStagingStyle =
  | 'modern'
  | 'scandinavian'
  | 'coastal'
  | 'luxury'
  | 'minimalist';

export interface FullStageResult {
  photos: PropertyPhoto[];
  successCount: number;
  errorMessage?: string;
}

/**
 * Run Full Virtual Staging on the photos that have `enhancementOptions.fullStaging = true`.
 * The edge function furnishes empty rooms with AI-generated furniture. Called AFTER
 * standard enhancement so both effects can be combined on the same photo.
 *
 * Consumes 1 unit of the FULL_STAGING monthly quota (per property, not per photo).
 * Returns the updated photos array with the newly staged URLs replacing `enhanced`.
 */
export async function fullStageEmptyRooms(
  photos: PropertyPhoto[],
): Promise<FullStageResult> {
  const targets = photos
    .map((p, index) => ({ p, index }))
    .filter(({ p }) => p.enhancementOptions?.fullStaging && (p.enhanced || p.preview));

  if (targets.length === 0) {
    return { photos, successCount: 0 };
  }

  const requestImages = targets.map(({ p }) => ({
    url: (p.enhanced || p.preview)!,
    style: (p.enhancementOptions?.fullStagingStyle as FullStagingStyle) || 'modern',
    // Prefer the user's explicit room-type choice from the staging picker over
    // the auto-detected roomType — the AI mis-classifies too often on its own.
    roomType: p.enhancementOptions?.fullStagingRoomType || p.roomType,
  }));

  const { data, error } = await supabase.functions.invoke(
    'virtual-stage-empty-room',
    { body: { images: requestImages } },
  );

  if (error) {
    console.error('Full virtual staging failed:', error);
    return {
      photos,
      successCount: 0,
      errorMessage: error.message || 'Full virtual staging failed',
    };
  }

  if (data?.error) {
    return {
      photos,
      successCount: 0,
      errorMessage: data.error as string,
    };
  }

  const staged = (data?.stagedImages as (string | null)[] | undefined) || [];
  const nextPhotos = [...photos];
  let successCount = 0;

  targets.forEach(({ index }, i) => {
    const stagedUrl = staged[i];
    if (stagedUrl && stagedUrl.startsWith('data:image/')) {
      nextPhotos[index] = {
        ...nextPhotos[index],
        enhanced: stagedUrl,
      };
      successCount += 1;
    }
  });

  if (successCount === 0) {
    return {
      photos,
      successCount: 0,
      errorMessage:
        'Full virtual staging could not generate any images this time. Please try again — if it keeps failing, contact support so we can refund the credit.',
    };
  }

  return { photos: nextPhotos, successCount };
}